/**
 * Core Cursor Position Management System
 * 
 * Provides intelligent cursor position preservation and management during field
 * formatting operations. Handles edge cases like text selection, multi-character
 * operations, and mobile touch input behavior.
 */

import { FieldType } from '../detection-patterns';

/**
 * Cursor position information
 */
export interface CursorPosition {
  /** Cursor start position (for caret or selection start) */
  start: number;
  /** Cursor end position (for selection end, same as start for caret) */
  end: number;
  /** Direction of selection (-1 = backward, 1 = forward, 0 = no selection) */
  direction: 'forward' | 'backward' | 'none';
}

/**
 * Text selection information
 */
export interface TextSelection {
  /** Selected text content */
  text: string;
  /** Selection start position */
  start: number;
  /** Selection end position */
  end: number;
  /** Whether there's an active selection */
  hasSelection: boolean;
}

/**
 * Cursor operation context
 */
export interface CursorOperationContext {
  /** Original text value before operation */
  originalValue: string;
  /** New text value after operation */
  newValue: string;
  /** Original cursor position */
  originalCursor: CursorPosition;
  /** Type of operation performed */
  operationType: CursorOperationType;
  /** Field type being processed */
  fieldType: FieldType;
  /** Whether this is a mobile device */
  isMobile: boolean;
  /** Any text selection present */
  selection?: TextSelection;
}

/**
 * Types of cursor operations
 */
export enum CursorOperationType {
  /** Single character input */
  INPUT = 'input',
  /** Text deletion (backspace/delete) */
  DELETE = 'delete',
  /** Multi-character paste operation */
  PASTE = 'paste',
  /** Format application during typing */
  FORMAT_PROGRESSIVE = 'format_progressive',
  /** Format application on blur */
  FORMAT_COMPLETE = 'format_complete',
  /** Text selection and replacement */
  SELECTION_REPLACE = 'selection_replace',
  /** Programmatic value change */
  PROGRAMMATIC = 'programmatic',
  /** Undo/redo operation */
  UNDO_REDO = 'undo_redo'
}

/**
 * Result of cursor position calculation
 */
export interface CursorCalculationResult {
  /** Calculated new cursor position */
  newCursor: CursorPosition;
  /** Whether cursor position is reliable */
  isReliable: boolean;
  /** Reason for cursor positioning decision */
  reason: string;
  /** Fallback position if primary calculation fails */
  fallback: number;
}

/**
 * Cursor positioning strategy
 */
export interface CursorStrategy {
  /** Strategy name */
  name: string;
  /** Whether strategy applies to context */
  applies(context: CursorOperationContext): boolean;
  /** Calculate cursor position */
  calculate(context: CursorOperationContext): CursorCalculationResult;
  /** Priority (higher = preferred) */
  priority: number;
}

/**
 * Core Cursor Manager Class
 * 
 * Manages cursor position preservation during field formatting operations.
 * Uses strategy pattern to handle different operation types and edge cases.
 */
export class CursorManager {
  private readonly strategies: CursorStrategy[] = [];
  private readonly debugMode: boolean;
  
  // Performance tracking
  private operationCount: number = 0;
  private totalCalculationTime: number = 0;

  constructor(debugMode: boolean = false) {
    this.debugMode = debugMode;
    this.initializeStrategies();
    
    if (this.debugMode) {
      this.log(`CursorManager initialized with strategies: ${this.strategies.map(s => s.name).join(', ')}`);
    }
  }

  /**
   * Calculate new cursor position after a formatting operation
   * 
   * @param context The operation context
   * @returns Calculated cursor position result
   */
  public calculateCursorPosition(context: CursorOperationContext): CursorCalculationResult {
    const startTime = performance.now();
    
    try {
      // Find applicable strategies, sorted by priority
      const applicableStrategies = this.strategies
        .filter(strategy => strategy.applies(context))
        .sort((a, b) => b.priority - a.priority);

      if (applicableStrategies.length === 0) {
        return this.getFallbackResult(context, 'No applicable strategies found');
      }

      // Try strategies in priority order
      for (const strategy of applicableStrategies) {
        try {
          const result = strategy.calculate(context);
          
          if (this.debugMode) {
            this.log(`Strategy '${strategy.name}' calculated cursor position: newCursor=${JSON.stringify(result.newCursor)}, isReliable=${result.isReliable}, reason=${result.reason}`);
          }

          // Return first reliable result
          if (result.isReliable) {
            this.trackPerformance(performance.now() - startTime);
            return result;
          }
          
        } catch (error) {
          this.log(`Strategy '${strategy.name}' failed: ${error}`, 'warn');
        }
      }

      // No reliable results, return last attempt or fallback
      const lastStrategy = applicableStrategies[applicableStrategies.length - 1];
      const fallbackResult = this.getFallbackResult(context, `All strategies unreliable, using fallback`);
      
      this.trackPerformance(performance.now() - startTime);
      return fallbackResult;

    } catch (error) {
      this.log(`CursorManager.calculateCursorPosition error: ${error}`, 'error');
      const fallbackResult = this.getFallbackResult(context, `Error during calculation: ${error}`);
      this.trackPerformance(performance.now() - startTime);
      return fallbackResult;
    }
  }

  /**
   * Get cursor position from DOM element
   * 
   * @param element The input element
   * @returns Current cursor position
   */
  public getCursorPosition(element: HTMLInputElement | HTMLTextAreaElement): CursorPosition {
    try {
      const start = element.selectionStart || 0;
      const end = element.selectionEnd || 0;
      
      // Determine selection direction
      let direction: 'forward' | 'backward' | 'none' = 'none';
      if (start !== end) {
        // Try to determine direction using selection direction API if available
        try {
          const selectionDirection = (element as any).selectionDirection;
          if (selectionDirection === 'forward') {
            direction = 'forward';
          } else if (selectionDirection === 'backward') {
            direction = 'backward';
          } else {
            direction = 'forward'; // Default assumption
          }
        } catch {
          direction = 'forward'; // Fallback
        }
      }

      return {
        start: Math.min(start, end),
        end: Math.max(start, end),
        direction
      };

    } catch (error) {
      this.log(`Error getting cursor position: ${error}`, 'warn');
      return {
        start: 0,
        end: 0,
        direction: 'none'
      };
    }
  }

  /**
   * Set cursor position on DOM element
   * 
   * @param element The input element
   * @param position The cursor position to set
   * @param delay Optional delay for setting (useful for mobile)
   */
  public setCursorPosition(
    element: HTMLInputElement | HTMLTextAreaElement, 
    position: CursorPosition,
    delay: number = 0
  ): void {
    const setCursor = () => {
      try {
        // Ensure element is focused
        if (document.activeElement !== element) {
          element.focus();
        }

        // Set cursor position
        element.setSelectionRange(position.start, position.end, position.direction || 'none');
        
        if (this.debugMode) {
          this.log(`Cursor position set: ${JSON.stringify(position)}`);
        }

      } catch (error) {
        this.log(`Error setting cursor position: ${error}`, 'warn');
      }
    };

    if (delay > 0) {
      setTimeout(setCursor, delay);
    } else {
      setCursor();
    }
  }

  /**
   * Get text selection from element
   * 
   * @param element The input element
   * @returns Text selection information
   */
  public getTextSelection(element: HTMLInputElement | HTMLTextAreaElement): TextSelection {
    const cursorPos = this.getCursorPosition(element);
    const hasSelection = cursorPos.start !== cursorPos.end;
    
    let selectedText = '';
    if (hasSelection) {
      try {
        selectedText = element.value.substring(cursorPos.start, cursorPos.end);
      } catch (error) {
        this.log(`Error getting selected text: ${error}`, 'warn');
      }
    }

    return {
      text: selectedText,
      start: cursorPos.start,
      end: cursorPos.end,
      hasSelection
    };
  }

  /**
   * Check if cursor is at a format boundary (e.g., before/after a delimiter)
   * 
   * @param position Cursor position
   * @param value Text value
   * @param delimiters Format delimiters to check
   * @returns Whether cursor is at a format boundary
   */
  public isAtFormatBoundary(position: number, value: string, delimiters: string[]): boolean {
    if (position < 0 || position > value.length) {
      return false;
    }

    // Check character at cursor position and adjacent positions
    const charAtCursor = position < value.length ? value[position] : '';
    const charBefore = position > 0 ? value[position - 1] : '';
    const charAfter = position < value.length ? value[position + 1] : '';

    return delimiters.includes(charAtCursor) ||
           delimiters.includes(charBefore) ||
           delimiters.includes(charAfter);
  }

  /**
   * Get performance statistics
   */
  public getPerformanceStats(): {
    operationCount: number;
    averageCalculationTime: number;
    totalCalculationTime: number;
  } {
    return {
      operationCount: this.operationCount,
      averageCalculationTime: this.operationCount > 0 ? this.totalCalculationTime / this.operationCount : 0,
      totalCalculationTime: this.totalCalculationTime
    };
  }

  /**
   * Reset performance statistics
   */
  public resetPerformanceStats(): void {
    this.operationCount = 0;
    this.totalCalculationTime = 0;
  }

  // Private methods

  /**
   * Initialize cursor positioning strategies
   */
  private initializeStrategies(): void {
    // Import and register strategies
    // These will be implemented in separate strategy files
    this.strategies.push(
      // High priority strategies for common operations
      new InputCharacterStrategy(),
      new DeleteOperationStrategy(), 
      new PasteOperationStrategy(),
      new SelectionReplaceStrategy(),
      
      // Format-specific strategies
      new ProgressiveFormatStrategy(),
      new CompleteFormatStrategy(),
      
      // Mobile-specific strategies
      new MobileInputStrategy(),
      new TouchSelectionStrategy(),
      
      // Fallback strategies
      new BoundaryAdjustmentStrategy(),
      new FallbackEndStrategy()
    );
  }

  /**
   * Get fallback cursor position result
   */
  private getFallbackResult(context: CursorOperationContext, reason: string): CursorCalculationResult {
    // Safe fallback - place cursor at end of new value
    const fallbackPosition = context.newValue.length;
    
    return {
      newCursor: {
        start: fallbackPosition,
        end: fallbackPosition,
        direction: 'none'
      },
      isReliable: false,
      reason,
      fallback: fallbackPosition
    };
  }

  /**
   * Track performance metrics
   */
  private trackPerformance(calculationTime: number): void {
    this.operationCount++;
    this.totalCalculationTime += calculationTime;
  }

  /**
   * Debug logging utility
   */
  private log(message: string, level: 'log' | 'warn' | 'error' = 'log', data?: any): void {
    if (this.debugMode) {
      const prefix = '[CursorManager]';
      if (data) {
        console[level](`${prefix} ${message}`, data);
      } else {
        console[level](`${prefix} ${message}`);
      }
    }
  }
}

// Strategy implementations - these are placeholder classes that will be fully implemented
// in separate files for better organization

/**
 * Strategy for single character input operations
 */
class InputCharacterStrategy implements CursorStrategy {
  name = 'InputCharacterStrategy';
  priority = 100;

  applies(context: CursorOperationContext): boolean {
    return context.operationType === CursorOperationType.INPUT &&
           Math.abs(context.newValue.length - context.originalValue.length) <= 1;
  }

  calculate(context: CursorOperationContext): CursorCalculationResult {
    // Move cursor forward by the difference in length
    const lengthDiff = context.newValue.length - context.originalValue.length;
    const newPosition = Math.max(0, Math.min(
      context.originalCursor.start + lengthDiff,
      context.newValue.length
    ));

    return {
      newCursor: {
        start: newPosition,
        end: newPosition,
        direction: 'none'
      },
      isReliable: true,
      reason: 'Single character input with length adjustment',
      fallback: context.newValue.length
    };
  }
}

/**
 * Strategy for delete operations
 */
class DeleteOperationStrategy implements CursorStrategy {
  name = 'DeleteOperationStrategy';
  priority = 90;

  applies(context: CursorOperationContext): boolean {
    return context.operationType === CursorOperationType.DELETE;
  }

  calculate(context: CursorOperationContext): CursorCalculationResult {
    // Keep cursor at the same position after deletion
    const newPosition = Math.max(0, Math.min(
      context.originalCursor.start,
      context.newValue.length
    ));

    return {
      newCursor: {
        start: newPosition,
        end: newPosition,
        direction: 'none'
      },
      isReliable: true,
      reason: 'Maintain position after deletion',
      fallback: 0
    };
  }
}

/**
 * Strategy for paste operations
 */
class PasteOperationStrategy implements CursorStrategy {
  name = 'PasteOperationStrategy';
  priority = 85;

  applies(context: CursorOperationContext): boolean {
    return context.operationType === CursorOperationType.PASTE;
  }

  calculate(context: CursorOperationContext): CursorCalculationResult {
    // Place cursor at end after paste and format
    const newPosition = context.newValue.length;

    return {
      newCursor: {
        start: newPosition,
        end: newPosition,
        direction: 'none'
      },
      isReliable: true,
      reason: 'Cursor at end after paste operation',
      fallback: newPosition
    };
  }
}

/**
 * Strategy for selection replacement
 */
class SelectionReplaceStrategy implements CursorStrategy {
  name = 'SelectionReplaceStrategy';
  priority = 95;

  applies(context: CursorOperationContext): boolean {
    return context.operationType === CursorOperationType.SELECTION_REPLACE ||
           (Boolean(context.selection?.hasSelection) && context.operationType === CursorOperationType.INPUT);
  }

  calculate(context: CursorOperationContext): CursorCalculationResult {
    // Calculate based on replacement content length
    const replacementLength = context.newValue.length - context.originalValue.length + 
                             (context.selection?.end || 0) - (context.selection?.start || 0);
    
    const newPosition = Math.max(0, Math.min(
      (context.selection?.start || 0) + Math.max(0, replacementLength),
      context.newValue.length
    ));

    return {
      newCursor: {
        start: newPosition,
        end: newPosition,
        direction: 'none'
      },
      isReliable: true,
      reason: 'Selection replacement positioning',
      fallback: context.newValue.length
    };
  }
}

/**
 * Strategy for progressive formatting
 */
class ProgressiveFormatStrategy implements CursorStrategy {
  name = 'ProgressiveFormatStrategy';
  priority = 80;

  applies(context: CursorOperationContext): boolean {
    return context.operationType === CursorOperationType.FORMAT_PROGRESSIVE;
  }

  calculate(context: CursorOperationContext): CursorCalculationResult {
    // This will use position calculation utilities for format-aware positioning
    // For now, maintain relative position
    const relativePosition = context.originalCursor.start / Math.max(1, context.originalValue.length);
    const newPosition = Math.round(relativePosition * context.newValue.length);

    return {
      newCursor: {
        start: Math.min(newPosition, context.newValue.length),
        end: Math.min(newPosition, context.newValue.length),
        direction: 'none'
      },
      isReliable: false, // Needs more sophisticated calculation
      reason: 'Progressive formatting with relative positioning',
      fallback: context.newValue.length
    };
  }
}

/**
 * Strategy for complete formatting
 */
class CompleteFormatStrategy implements CursorStrategy {
  name = 'CompleteFormatStrategy';
  priority = 70;

  applies(context: CursorOperationContext): boolean {
    return context.operationType === CursorOperationType.FORMAT_COMPLETE;
  }

  calculate(context: CursorOperationContext): CursorCalculationResult {
    // Place cursor at end for complete formatting
    return {
      newCursor: {
        start: context.newValue.length,
        end: context.newValue.length,
        direction: 'none'
      },
      isReliable: true,
      reason: 'Complete formatting - cursor at end',
      fallback: context.newValue.length
    };
  }
}

/**
 * Mobile-specific input strategy
 */
class MobileInputStrategy implements CursorStrategy {
  name = 'MobileInputStrategy';
  priority = 60;

  applies(context: CursorOperationContext): boolean {
    return context.isMobile && context.operationType === CursorOperationType.INPUT;
  }

  calculate(context: CursorOperationContext): CursorCalculationResult {
    // Mobile devices may need different cursor handling due to virtual keyboards
    const newPosition = Math.min(
      context.originalCursor.start + (context.newValue.length - context.originalValue.length),
      context.newValue.length
    );

    return {
      newCursor: {
        start: newPosition,
        end: newPosition,
        direction: 'none'
      },
      isReliable: true,
      reason: 'Mobile-optimized input positioning',
      fallback: context.newValue.length
    };
  }
}

/**
 * Touch selection strategy
 */
class TouchSelectionStrategy implements CursorStrategy {
  name = 'TouchSelectionStrategy';
  priority = 55;

  applies(context: CursorOperationContext): boolean {
    return context.isMobile && Boolean(context.selection?.hasSelection);
  }

  calculate(context: CursorOperationContext): CursorCalculationResult {
    // Handle touch selection differently than mouse selection
    return {
      newCursor: {
        start: context.newValue.length,
        end: context.newValue.length,
        direction: 'none'
      },
      isReliable: false,
      reason: 'Touch selection fallback',
      fallback: context.newValue.length
    };
  }
}

/**
 * Boundary adjustment strategy
 */
class BoundaryAdjustmentStrategy implements CursorStrategy {
  name = 'BoundaryAdjustmentStrategy';
  priority = 40;

  applies(): boolean {
    return true; // Always applicable as fallback
  }

  calculate(context: CursorOperationContext): CursorCalculationResult {
    // Ensure cursor is within valid bounds
    const newPosition = Math.max(0, Math.min(
      context.originalCursor.start,
      context.newValue.length
    ));

    return {
      newCursor: {
        start: newPosition,
        end: newPosition,
        direction: 'none'
      },
      isReliable: false,
      reason: 'Boundary adjustment fallback',
      fallback: newPosition
    };
  }
}

/**
 * Ultimate fallback strategy
 */
class FallbackEndStrategy implements CursorStrategy {
  name = 'FallbackEndStrategy';
  priority = 0;

  applies(): boolean {
    return true; // Always applicable
  }

  calculate(context: CursorOperationContext): CursorCalculationResult {
    return {
      newCursor: {
        start: context.newValue.length,
        end: context.newValue.length,
        direction: 'none'
      },
      isReliable: false,
      reason: 'Ultimate fallback - cursor at end',
      fallback: context.newValue.length
    };
  }
}